import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
    Cog6ToothIcon,
    RectangleStackIcon,
    HomeIcon,
    BuildingOffice2Icon,
    GlobeAsiaAustraliaIcon,
    WrenchScrewdriverIcon,
    PlusIcon,
    BanknotesIcon,
    CurrencyDollarIcon,
    ChartBarIcon,
    ShieldCheckIcon,
    KeyIcon,
    CreditCardIcon,
    DocumentTextIcon,
    ClipboardDocumentCheckIcon,
    BriefcaseIcon,
    UserGroupIcon,
    BuildingLibraryIcon,
    TruckIcon,
    ScaleIcon,
    CalculatorIcon,
    PencilSquareIcon,
    CheckCircleIcon,
    BeakerIcon,
    CubeIcon,
    HeartIcon,
    StarIcon,
    LightBulbIcon,
    AcademicCapIcon,
    BookOpenIcon,
    ChatBubbleLeftRightIcon,
    PhoneIcon,
    EnvelopeIcon,
    MapPinIcon,
    ShoppingBagIcon,
    ReceiptPercentIcon,
    PresentationChartLineIcon,
    ArrowTrendingUpIcon,
    SparklesIcon,
    RocketLaunchIcon,
    BoltIcon,
    FireIcon,
    SunIcon,
    MoonIcon,
    CloudIcon,
    ClockIcon,
    CurrencyRupeeIcon
} from '@heroicons/react/24/outline';

const EMICalculatorPage = () => {
    const [activeTab, setActiveTab] = useState('settings');
    const [settings, setSettings] = useState(null);
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingSettings, setSavingSettings] = useState(false);

    // Fetch settings
    const fetchSettings = async () => {
        try {
            const response = await axios.get('/api/admin/emi-calculator/settings');
            const data = response.data.data;
            setSettings({
                section_title: data?.section_title || 'EMI Calculator',
                section_subtitle: data?.section_subtitle || 'Estimate your monthly payments and total cost instantly',
                cta_text: data?.cta_text || 'Apply Now',
                cta_link: data?.cta_link || '/customer-360-eligibility-calculator'
            });
        } catch (error) {
            console.error('Error fetching settings:', error);
            toast.error('Failed to load settings');
            // Provide defaults on failure so form still works
            setSettings({
                section_title: 'EMI Calculator',
                section_subtitle: 'Estimate your monthly payments and total cost instantly',
                cta_text: 'Apply Now',
                cta_link: '/customer-360-eligibility-calculator'
            });
        }
    };

    // Fetch cards
    const fetchCards = async () => {
        try {
            const response = await axios.get('/api/admin/emi-calculator/cards');
            setCards(response.data.data || []);
        } catch (error) {
            console.error('Error fetching cards:', error);
            toast.error('Failed to load cards');
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            await Promise.all([fetchSettings(), fetchCards()]);
            setLoading(false);
        };
        fetchData();
    }, []);

    // Settings handlers
    const handleSettingsChange = (field, value) => {
        setSettings(prev => ({ ...prev, [field]: value }));
    };

    const handleSettingsSave = async () => {
        try {
            setSavingSettings(true);
            await axios.put('/api/admin/emi-calculator/settings', settings);
            toast.success('Settings updated successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to save settings');
        } finally {
            setSavingSettings(false);
        }
    };

    // Card handlers
    const handleCardChange = (id, field, value) => {
        setCards(prev => prev.map(card =>
            card.id === id ? { ...card, [field]: value } : card
        ));
    };

    const handleCardSave = async (card) => {
        try {
            await axios.put(`/api/admin/emi-calculator/cards/${card.id}`, card);
            toast.success('Card updated successfully');
            fetchCards();
        } catch (error) {
            console.error('Error saving card:', error);
            toast.error('Failed to save card');
        }
    };

    const iconOptions = [
        { name: 'HomeIcon', icon: HomeIcon, label: 'Home' },
        { name: 'BuildingOffice2Icon', icon: BuildingOffice2Icon, label: 'Building' },
        { name: 'BuildingLibraryIcon', icon: BuildingLibraryIcon, label: 'Library' },
        { name: 'BanknotesIcon', icon: BanknotesIcon, label: 'Banknotes' },
        { name: 'CurrencyDollarIcon', icon: CurrencyDollarIcon, label: 'Dollar' },
        { name: 'CurrencyRupeeIcon', icon: CurrencyRupeeIcon, label: 'Rupee' },
        { name: 'CreditCardIcon', icon: CreditCardIcon, label: 'Credit Card' },
        { name: 'ReceiptPercentIcon', icon: ReceiptPercentIcon, label: 'Receipt' },
        { name: 'ChartBarIcon', icon: ChartBarIcon, label: 'Chart' },
        { name: 'PresentationChartLineIcon', icon: PresentationChartLineIcon, label: 'Presentation' },
        { name: 'ArrowTrendingUpIcon', icon: ArrowTrendingUpIcon, label: 'Trending Up' },
        { name: 'CalculatorIcon', icon: CalculatorIcon, label: 'Calculator' },
        { name: 'ClockIcon', icon: ClockIcon, label: 'Clock' },
        { name: 'ShieldCheckIcon', icon: ShieldCheckIcon, label: 'Shield' },
        { name: 'KeyIcon', icon: KeyIcon, label: 'Key' },
        { name: 'DocumentTextIcon', icon: DocumentTextIcon, label: 'Document' },
        { name: 'ClipboardDocumentCheckIcon', icon: ClipboardDocumentCheckIcon, label: 'Clipboard' },
        { name: 'BriefcaseIcon', icon: BriefcaseIcon, label: 'Briefcase' },
        { name: 'UserGroupIcon', icon: UserGroupIcon, label: 'Users' },
        { name: 'TruckIcon', icon: TruckIcon, label: 'Truck' },
        { name: 'ScaleIcon', icon: ScaleIcon, label: 'Scale' },
        { name: 'WrenchScrewdriverIcon', icon: WrenchScrewdriverIcon, label: 'Wrench' },
        { name: 'PencilSquareIcon', icon: PencilSquareIcon, label: 'Edit' },
        { name: 'CheckCircleIcon', icon: CheckCircleIcon, label: 'Check' },
        { name: 'BeakerIcon', icon: BeakerIcon, label: 'Beaker' },
        { name: 'CubeIcon', icon: CubeIcon, label: 'Cube' },
        { name: 'HeartIcon', icon: HeartIcon, label: 'Heart' },
        { name: 'StarIcon', icon: StarIcon, label: 'Star' },
        { name: 'LightBulbIcon', icon: LightBulbIcon, label: 'Bulb' },
        { name: 'AcademicCapIcon', icon: AcademicCapIcon, label: 'Academic' },
        { name: 'BookOpenIcon', icon: BookOpenIcon, label: 'Book' },
        { name: 'GlobeAsiaAustraliaIcon', icon: GlobeAsiaAustraliaIcon, label: 'Globe' },
        { name: 'ChatBubbleLeftRightIcon', icon: ChatBubbleLeftRightIcon, label: 'Chat' },
        { name: 'PhoneIcon', icon: PhoneIcon, label: 'Phone' },
        { name: 'EnvelopeIcon', icon: EnvelopeIcon, label: 'Email' },
        { name: 'MapPinIcon', icon: MapPinIcon, label: 'Location' },
        { name: 'ShoppingBagIcon', icon: ShoppingBagIcon, label: 'Shopping' },
        { name: 'SparklesIcon', icon: SparklesIcon, label: 'Sparkles' },
        { name: 'RocketLaunchIcon', icon: RocketLaunchIcon, label: 'Rocket' },
        { name: 'BoltIcon', icon: BoltIcon, label: 'Bolt' },
        { name: 'FireIcon', icon: FireIcon, label: 'Fire' },
        { name: 'SunIcon', icon: SunIcon, label: 'Sun' },
        { name: 'MoonIcon', icon: MoonIcon, label: 'Moon' },
        { name: 'CloudIcon', icon: CloudIcon, label: 'Cloud' },
        { name: 'PlusIcon', icon: PlusIcon, label: 'Plus' }
    ];
    const colorOptions = ['blue', 'orange', 'green', 'purple', 'red', 'teal'];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-slate-800">EMI Calculator Management</h1>
                <p className="text-slate-500 mt-1">Manage calculator section settings and result cards</p>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-1.5 inline-flex gap-1">
                <button
                    onClick={() => setActiveTab('settings')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${activeTab === 'settings'
                        ? 'bg-primary-600 text-white shadow-lg'
                        : 'text-slate-600 hover:bg-slate-50'
                        }`}
                >
                    <Cog6ToothIcon className="h-5 w-5" />
                    Settings
                </button>
                <button
                    onClick={() => setActiveTab('cards')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${activeTab === 'cards'
                        ? 'bg-primary-600 text-white shadow-lg'
                        : 'text-slate-600 hover:bg-slate-50'
                        }`}
                >
                    <RectangleStackIcon className="h-5 w-5" />
                    Result Cards ({cards.length})
                </button>
            </div>

            {/* Settings Tab */}
            {activeTab === 'settings' && settings && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
                    <h2 className="text-xl font-bold text-slate-800 mb-6">Section Settings</h2>
                    <div className="space-y-6 max-w-2xl">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Section Title
                            </label>
                            <input
                                type="text"
                                value={settings.section_title}
                                onChange={(e) => handleSettingsChange('section_title', e.target.value)}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Section Subtitle
                            </label>
                            <textarea
                                value={settings.section_subtitle}
                                onChange={(e) => handleSettingsChange('section_subtitle', e.target.value)}
                                rows={3}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                CTA Button Text
                            </label>
                            <input
                                type="text"
                                value={settings.cta_text}
                                onChange={(e) => handleSettingsChange('cta_text', e.target.value)}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Button Link
                            </label>
                            <input
                                type="text"
                                value={settings.cta_link}
                                onChange={(e) => handleSettingsChange('cta_link', e.target.value)}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                        </div>
                        <div className="pt-4">
                            <button
                                onClick={handleSettingsSave}
                                disabled={savingSettings}
                                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-200 disabled:opacity-50"
                            >
                                {savingSettings ? 'Saving...' : 'Save Settings'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cards Tab */}
            {activeTab === 'cards' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {cards.map((card) => (
                        <div key={card.id} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                                        Card Title
                                    </label>
                                    <input
                                        type="text"
                                        value={card.title}
                                        onChange={(e) => handleCardChange(card.id, 'title', e.target.value)}
                                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                                        Icon (HeroIcons v2 Outline)
                                    </label>
                                    <select
                                        value={card.icon_name}
                                        onChange={(e) => handleCardChange(card.id, 'icon_name', e.target.value)}
                                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    >
                                        {iconOptions.map(option => (
                                            <option key={option.name} value={option.name}>{option.label}</option>
                                        ))}
                                    </select>
                                    {/* Icon Preview */}
                                    <div className="mt-2 flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                                        <span className="text-xs font-medium text-slate-600">Preview:</span>
                                        {(() => {
                                            const selectedIcon = iconOptions.find(opt => opt.name === card.icon_name);
                                            const Icon = selectedIcon ? selectedIcon.icon : ClockIcon;
                                            return <Icon className="h-5 w-5 text-primary-600" />;
                                        })()}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                                        Color
                                    </label>
                                    <select
                                        value={card.color_class}
                                        onChange={(e) => handleCardChange(card.id, 'color_class', e.target.value)}
                                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    >
                                        {colorOptions.map(color => (
                                            <option key={color} value={color}>{color}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                                        Display Order
                                    </label>
                                    <input
                                        type="number"
                                        value={card.display_order}
                                        onChange={(e) => handleCardChange(card.id, 'display_order', parseInt(e.target.value))}
                                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    />
                                </div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={card.is_active}
                                        onChange={(e) => handleCardChange(card.id, 'is_active', e.target.checked)}
                                        className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                                    />
                                    <label className="ml-2 text-sm font-semibold text-slate-700">
                                        Active
                                    </label>
                                </div>
                                <button
                                    onClick={() => handleCardSave(card)}
                                    className="w-full bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-xl font-semibold transition-all"
                                >
                                    Save Card
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default EMICalculatorPage;
